import { useContext } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { PollingStatus } from '@/types/order';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useIsLandscape } from '@/hooks/useIsLandscape';
import { cn } from '@/utils/cn';
import { Modal } from '@/components/Common/Modal';
import { TopWrapper } from '@/components/TopWrapper/TopWrapper';
import { XButton } from '@/components/XButton/XButton';
import { Payment } from '@/components/Payment/Payment';
import { Card3DSForm } from '@/components/Card3DSForm/Card3DSForm';
import { ProcessingLoader } from '@/components/ProcessingLoader/ProcessingLoader';
import { ScreenDrawer } from '@/components/ScreenDrawer/ScreenDrawer';
import { RedirectPayment } from '@/components/RedirectPayment/RedirectPayment';
import { SuccessScreen } from '@/components/SuccessScreen/SuccessScreen';
import type { TemplateViewProps } from '@/templates/CheckoutTemplate';
import { PromoCode } from '@/components/PromoCode/PromoCode';
/**
 * Full-screen checkout view: TopWrapper hero on the left, payment column
 * on the right. Used by the DEFAULT template and as the desktop fallback
 * inside {@link PreferredPaymentView}.
 */
export const DefaultView = ({ statusOrderPolling }: TemplateViewProps) => {
  const context = useContext(CheckoutContext);
  const {
    actions: { closeCheckout },
    successScreen,
    redirectPayment: { redirectUrl, showRedirect },
  } = context;
  const isMobile = useIsMobile();
  const landscape = useIsLandscape();
  return (
    <Modal>
      <div
        data-testid="checkout-container"
        className={cn(
          'flex flex-col md:flex-row md:justify-start md:h-full overflow-y-auto md:overflow-y-hidden max-md:overflow-x-hidden bg-white landscape:flex-row landscape:h-[100dvh] landscape:overflow-y-hidden landscape:overflow-x-hidden',
          // Mobile portrait: the modal is full-height, so the column must fill it
          // (`h-full`) and own the scroll. Combined with the `flex-1` payment
          // column below and the `shrink-0` hero, this pins the Footer to the
          // bottom when content fits and scrolls naturally when it doesn't —
          // matching OnePaymentView / PreferredPaymentView. On larger viewports
          // the original `sm:justify-center` + `md:h-full` behavior is preserved.
          isMobile ? 'h-full' : 'sm:justify-center'
        )}
      >
        <TopWrapper />
        {!landscape && isMobile && <PromoCode />}
        <div
          className={cn(
            'relative flex flex-col md:w-3/5 md:h-full md:overflow-y-auto landscape:w-[56%] landscape:h-[100dvh] landscape:overflow-y-auto',
            isMobile && 'flex-1'
          )}
        >
          {(!isMobile || landscape) && <XButton onClick={() => closeCheckout(context)} />}
          <Payment />
        </div>
        <Card3DSForm />
        <ProcessingLoader />
        {isMobile && <ScreenDrawer />}
        {(redirectUrl || showRedirect) && <RedirectPayment />}
        {successScreen?.isOpen && statusOrderPolling === PollingStatus.SUCCESS && <SuccessScreen />}
      </div>
    </Modal>
  );
};
